/**
 * Unknown-sender triage queue (SPEC §7.1, Decision #17 + design batch A).
 *
 * Master-detail: pending list on the left, selected detail + action
 * tray on the right. Four resolution actions:
 *   - Add @domain to existing organisation → materialises the parked
 *     email as a ticket on that org's active contract.
 *   - Create new organisation with this domain (no ticket; admin
 *     follows up via the contract wizard).
 *   - Attach to existing ticket — for the [T-1039] subject-tag-with-
 *     unrecognised-domain case.
 *   - Dismiss as misdirected.
 *
 * No auto-reply is ever sent from this surface. SLA clocks don't run
 * on parked emails.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Building2, Link2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import type { UnknownEmailRow } from '@beacon/api-client';

import { Button } from '@/components/ui/button.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { Input } from '@/components/ui/input.js';
import { SkeletonList } from '@/components/ui/skeleton.js';
import { useOrganizations } from '@/hooks/use-organizations.js';
import {
  useUnknownEmails,
  useResolveByAddDomain,
  useResolveByCreateOrg,
  useResolveByAttachTicket,
  useDismissUnknownEmail,
} from '@/hooks/use-unknown-emails.js';
import { cn } from '@/lib/cn.js';

export function UnknownSenders() {
  const queue = useUnknownEmails('pending');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = queue.data ?? [];
  const selected = rows.find((r) => r.id === selectedId) ?? rows[0] ?? null;

  // Auto-select first row when the queue resolves and nothing's selected.
  useEffect(() => {
    if (!selectedId && rows[0]) setSelectedId(rows[0].id);
    // If the selected row was just resolved (filtered out), pick the next one.
    if (selectedId && !rows.some((r) => r.id === selectedId)) {
      setSelectedId(rows[0]?.id ?? null);
    }
  }, [rows, selectedId]);

  return (
    <div className="flex h-full min-h-0">
      <div className="w-[420px] shrink-0 border-r border-ink-600 flex flex-col bg-ink-900">
        <header className="px-5 pt-4 pb-3 border-b border-ink-600 bg-ink-850">
          <div className="text-[12px] text-ink-300 mb-1">Inbox</div>
          <div className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-md bg-warn-500/15 text-warn-400 border border-warn-500/30 inline-flex items-center justify-center shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <h1 className="text-[16px] font-semibold">Unknown senders</h1>
              <div className="text-[12px] text-ink-300">
                Not on any contract ·{' '}
                <span className="mono text-warn-400">no auto-reply sent</span>{' '}
                · SLA does not run
              </div>
            </div>
            <span className="mono text-[11.5px] text-ink-300 bg-ink-700 px-1.5 h-5 rounded inline-flex items-center self-start">
              {rows.length}
            </span>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          {queue.isLoading ? (
            <SkeletonList count={4} rowHeight={72} />
          ) : rows.length === 0 ? (
            <EmptyState
              tone="celebrate"
              icon={<AlertTriangle className="h-5 w-5" />}
              heading="Queue clear"
              sub="All inbound mail is matching a known contract. Anything from an unrecognised sender will land here."
            />
          ) : (
            rows.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                data-testid={`unk-row-${r.id}`}
                className={cn(
                  'w-full text-left px-5 py-3 border-b border-ink-600/60 block focus-ring',
                  selected?.id === r.id
                    ? 'bg-beacon-400/[0.06] border-l-2 border-l-beacon-400 pl-[18px]'
                    : 'hover:bg-ink-800/60'
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[13px] font-medium text-ink-100 truncate">
                    {r.fromName ?? r.fromEmail}
                  </span>
                  <span className="ml-auto mono text-[11px] text-ink-400 shrink-0">
                    {formatRelative(r.receivedAt)}
                  </span>
                </div>
                <div className="text-[12.5px] text-ink-200 truncate mb-1">
                  {r.subject}
                </div>
                <div className="text-[11.5px] text-ink-400 truncate mb-1.5">
                  {r.bodyText.split('\n')[0]?.slice(0, 100) ?? ''}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="mono text-[10.5px] text-ink-400 truncate">
                    @{domainOf(r.fromEmail)}
                  </span>
                  {r.hadAttachments && (
                    <span className="ml-auto text-[10.5px] mono text-ink-400">
                      📎 dropped
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 min-w-0 flex flex-col bg-ink-900">
        {selected ? (
          <Detail key={selected.id} row={selected} />
        ) : (
          <EmptyState
            tone="muted"
            icon={<AlertTriangle className="h-5 w-5" />}
            heading="Nothing to triage"
            sub="When emails arrive from unrecognised senders, they'll show up here."
          />
        )}
      </div>
    </div>
  );
}

function Detail({ row }: { row: UnknownEmailRow }) {
  const [orgPickerId, setOrgPickerId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [ticketRef, setTicketRef] = useState('');

  const orgs = useOrganizations();
  const addDomain = useResolveByAddDomain();
  const createOrg = useResolveByCreateOrg();
  const attachTicket = useResolveByAttachTicket();
  const dismiss = useDismissUnknownEmail();

  const busy =
    addDomain.isPending ||
    createOrg.isPending ||
    attachTicket.isPending ||
    dismiss.isPending;

  // Pre-pick the first client org so the dropdown isn't empty by default.
  const clientOrgs = useMemo(
    () => (orgs.data ?? []).filter((o) => o.type === 'client'),
    [orgs.data]
  );
  useEffect(() => {
    if (orgPickerId && clientOrgs.some((o) => o.id === orgPickerId)) return;
    if (clientOrgs[0]) setOrgPickerId(clientOrgs[0].id);
  }, [clientOrgs, orgPickerId]);

  const domain = domainOf(row.fromEmail);

  function handleAddDomain() {
    if (!orgPickerId) return toast.error('Pick an organisation');
    addDomain.mutate(
      { id: row.id, organizationId: orgPickerId },
      {
        onSuccess: (res) => {
          if (res.ticketNumber) {
            toast.success(`Created ${res.ticketNumber}`);
          } else {
            toast.success('Domain added');
          }
        },
      }
    );
  }
  function handleCreateOrg() {
    if (!orgName.trim()) return toast.error('Organisation name required');
    createOrg.mutate(
      { id: row.id, organizationName: orgName.trim() },
      {
        onSuccess: () => toast.success('Organisation created'),
      }
    );
  }
  function handleAttachTicket() {
    const num = ticketRef.trim().toUpperCase();
    if (!/^T-\d+$/.test(num)) {
      return toast.error('Expected a ticket number like T-1042');
    }
    attachTicket.mutate(
      { id: row.id, ticketNumber: num },
      {
        onSuccess: () => toast.success(`Attached to ${num}`),
      }
    );
  }
  function handleDismiss() {
    dismiss.mutate(row.id, {
      onSuccess: () => toast.success('Dismissed'),
    });
  }

  return (
    <>
      <div className="px-6 pt-5 pb-4 border-b border-ink-600 bg-ink-850">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 h-5 px-2 rounded bg-warn-500/15 text-warn-400 border border-warn-500/30 text-[10.5px] uppercase tracking-wider font-semibold">
            Unknown sender
          </span>
          <span className="mono text-[11.5px] text-ink-300 ml-2">
            Received {formatAbsolute(row.receivedAt)} · held by Beacon, not
            forwarded
          </span>
        </div>
        <h2 className="text-[18px] font-semibold leading-snug">
          {row.subject}
        </h2>
        <div className="mt-2 flex items-center gap-2 text-[12.5px] text-ink-300">
          <span className="text-ink-100 font-medium">
            {row.fromName ?? row.fromEmail}
          </span>
          <span className="mono text-ink-400">&lt;{row.fromEmail}&gt;</span>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-5">
        <div className="max-w-[680px]">
          <div className="bg-ink-850 border border-ink-600 rounded-lg px-5 py-4 text-[13.5px] text-ink-100 leading-relaxed whitespace-pre-wrap">
            {row.bodyText}
          </div>
          {row.hadAttachments && (
            <div className="mt-3 text-[11.5px] text-ink-400 mono">
              ⚠ This email had attachments — bytes were not retained in v1.
            </div>
          )}
          <div className="mt-4 text-[11px] text-ink-400 mono leading-relaxed">
            {row.emailMessageId && (
              <div>Message-ID: &lt;{row.emailMessageId}&gt;</div>
            )}
            {row.inReplyTo && (
              <div>In-Reply-To: &lt;{row.inReplyTo}&gt;</div>
            )}
            <div>From domain: {domain ?? '(none)'}</div>
          </div>
        </div>
      </div>

      <div className="border-t border-ink-600 bg-ink-850 px-6 py-3 space-y-3">
        <div className="flex items-end gap-2 flex-wrap">
          <Field label={`Add @${domain ?? '...'} to`}>
            <select
              value={orgPickerId}
              onChange={(e) => setOrgPickerId(e.target.value)}
              disabled={busy || orgs.isLoading}
              className="h-8 px-2 text-[12.5px] bg-ink-800 border border-ink-600 rounded-md min-w-[180px] focus-ring"
              data-testid="unk-org-pick"
            >
              {clientOrgs.length === 0 && (
                <option value="">— no client orgs —</option>
              )}
              {clientOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </Field>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAddDomain}
            disabled={busy || !orgPickerId}
            data-testid="unk-add-domain"
          >
            <Plus className="h-3.5 w-3.5" />
            {addDomain.isPending ? 'Adding…' : 'Add domain'}
          </Button>
          <span className="text-[11px] text-ink-400 mono">
            → creates ticket on org's active contract
          </span>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <Field label="Create new organisation">
            <Input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              disabled={busy}
              placeholder="e.g. Ironbark NZ"
              className="!h-8 !text-[12.5px] min-w-[220px]"
              data-testid="unk-new-org-name"
            />
          </Field>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCreateOrg}
            disabled={busy || !orgName.trim()}
            data-testid="unk-create-org"
          >
            <Building2 className="h-3.5 w-3.5" />
            {createOrg.isPending ? 'Creating…' : 'Create org'}
          </Button>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <Field label="Attach to existing ticket">
            <Input
              value={ticketRef}
              onChange={(e) => setTicketRef(e.target.value)}
              disabled={busy}
              placeholder="T-1042"
              className="!h-8 !text-[12.5px] mono w-32"
              data-testid="unk-ticket-ref"
            />
          </Field>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAttachTicket}
            disabled={busy || !ticketRef.trim()}
            data-testid="unk-attach-ticket"
          >
            <Link2 className="h-3.5 w-3.5" />
            {attachTicket.isPending ? 'Attaching…' : 'Attach'}
          </Button>
          <span className="ml-auto" />
          <Button
            variant="outline"
            size="sm"
            onClick={handleDismiss}
            disabled={busy}
            data-testid="unk-dismiss"
            className="text-danger-400 border-danger-500/30 hover:bg-danger-500/10"
          >
            <X className="h-3.5 w-3.5" />
            {dismiss.isPending ? 'Dismissing…' : 'Dismiss'}
          </Button>
        </div>

        <div className="text-[11px] text-ink-400 mono">
          Every action above is logged to the audit trail. No reply is sent
          to the sender.
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-[0.06em] text-ink-300 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function domainOf(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0 ? email : email.slice(at + 1);
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function formatAbsolute(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
